// 款式 (variants): a parent product holds the shared info (name, brand,
// category, description, photos); each variant (products.parent_id = parent)
// has its own option values, price, cost, SKU, barcode and stock.
// The parent never sells or counts stock itself (track_stock off); the
// register shows one tile and asks which 款式.
// Options live on the parent: web.options = [{ name: "尺寸", values: ["S","M"] }].
import { useMemo, useState } from "react";
import { zhEn } from "./posTier.js";
import { Plus, ScanLine, Trash2 } from "lucide-react";
import { C, Btn, Chip, Pill, Card, Sheet, Label, inputStyle, money } from "./posUi.jsx";
import { newId, effectivePrice } from "./posMath.js";
import { ProductThumb } from "./Register.jsx";

export const variantLabel = (v, options) =>
  (options || []).map(o => v.optionValues?.[o.name]).filter(Boolean).join(" / ") || Object.values(v.optionValues || {}).join(" / ");

// All combinations of the option values, as {name: value} maps.
export function combos(options) {
  const opts = (options || []).filter(o => o.name && o.values?.length);
  if (!opts.length) return [];
  return opts.reduce((acc, o) => acc.flatMap(a => o.values.map(v => ({ ...a, [o.name]: v }))), [{}]);
}
// A row matches a combination when every option it HAS agrees — so adding a
// second option keeps the existing rows (the missing value is filled in).
const sameCombo = (a, b, options) => (options || []).every(o => a?.[o.name] == null || a[o.name] === (b?.[o.name] || ""));

// Editor tab. `rows` are variant drafts: {id?, optionValues, price, cost, sku, barcode, stock (new only), status, _new}
export function VariantsTab({ parent, options, setOptions, rows, setRows, sym, onScanOpen, products }) {
  const [newVal, setNewVal] = useState({});
  const addOption = () => { if (options.length < 2) setOptions([...options, { name: options.length ? zhEn("口味", "Flavour") : zhEn("尺寸", "Size"), values: [] }]); };
  const setOpt = (i, patch) => {
    // Renaming an option renames that key on every existing 款式 (else they'd all be dropped).
    if (patch.name !== undefined && options[i].name && patch.name !== options[i].name) {
      const from = options[i].name, to = patch.name;
      setRows(rows.map(r => {
        if (!r.optionValues || !(from in r.optionValues)) return r;
        const ov = { ...r.optionValues }; ov[to] = ov[from]; delete ov[from];
        return { ...r, optionValues: ov };
      }));
    }
    setOptions(options.map((o, j) => j === i ? { ...o, ...patch } : o));
  };
  const addValue = (i) => {
    const v = (newVal[i] || "").trim();
    if (!v || options[i].values.includes(v)) return;
    setOpt(i, { values: [...options[i].values, v] }); setNewVal(x => ({ ...x, [i]: "" }));
  };
  // Keep one row per combination; existing rows keep their data.
  const [optErr, setOptErr] = useState("");
  const regenerate = () => {
    const names = options.map(o => o.name.trim());
    if (names.some(n => !n) || new Set(names).size !== names.length) { setOptErr(zhEn("每個選項要有唔同嘅名", "Each option needs a different name")); return; }
    setOptErr("");
    const want = combos(options);
    const used = new Set();
    const next = want.map(c => {
      const hit = rows.find(r => !used.has(r.id) && sameCombo(r.optionValues, c, options));
      if (hit) {
        used.add(hit.id);
        // Reused (maybe previously removed) row: back in play, values completed.
        return { ...hit, optionValues: { ...c }, _dropped: false, status: hit._dropped ? "active" : hit.status };
      }
      return { id: newId(), _new: true, optionValues: c, price: String(parent.price ?? ""), cost: String(parent.cost ?? ""), sku: "", barcode: "", stock: "", status: "active" };
    });
    const dropped = rows.filter(r => !r._new && !used.has(r.id));
    setRows([...next, ...dropped.map(r => ({ ...r, status: "draft", _dropped: true }))]);
  };
  const patch = (id, p) => setRows(rows.map(r => r.id === id ? { ...r, ...p } : r));
  const dupBarcode = (r) => r.barcode && (products.find(p => p.barcode === r.barcode.trim() && p.id !== r.id) || rows.find(x => x !== r && x.barcode && x.barcode.trim() === r.barcode.trim()));
  const live = rows.filter(r => !r._dropped);

  return (
    <div>
      <p style={{ fontSize: 12.5, fontWeight: 700, color: C.sub, margin: "0 2px 12px", lineHeight: 1.5 }}>
        {zhEn("同一件貨有唔同尺寸 / 口味 / 顏色？加款式，每款有自己嘅價錢、條碼同庫存。收銀會先揀款式。", "Same product in different sizes / flavours / colours? Add variants — each has its own price, barcode and stock. The till asks for the variant first.")}
      </p>
      {options.map((o, i) => (
        <Card key={i} style={{ padding: 12, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input value={o.name} onChange={e => setOpt(i, { name: e.target.value })} placeholder={zhEn("選項名，例如 尺寸", "Option name, e.g. Size")} style={{ ...inputStyle, flex: 1, padding: "9px 10px" }} />
            <button type="button" aria-label={zhEn("刪除選項", "Delete option")} onClick={() => setOptions(options.filter((_, j) => j !== i))} style={{ border: "none", background: "none", color: C.faint, cursor: "pointer" }}><Trash2 size={18} /></button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {o.values.map(v => <Chip key={v} on onClick={() => setOpt(i, { values: o.values.filter(x => x !== v) })}>{v} ✕</Chip>)}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={newVal[i] || ""} onChange={e => setNewVal(x => ({ ...x, [i]: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addValue(i); } }}
              placeholder={i === 0 ? zhEn("例如 500ml、1L、S、M", "e.g. 500ml, 1L, S, M") : zhEn("例如 雞肉、三文魚", "e.g. Chicken, Salmon")} style={{ ...inputStyle, flex: 1, padding: "9px 10px" }} />
            <Btn small kind="soft" onClick={() => addValue(i)}><Plus size={15} /> {zhEn("加", "Add")}</Btn>
          </div>
        </Card>
      ))}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {options.length < 2 && <Btn small kind="ghost" onClick={addOption}><Plus size={15} /> {options.length ? zhEn("加第二個選項", "Add a second option") : zhEn("加選項（例如 尺寸）", "Add an option (e.g. Size)")}</Btn>}
        {combos(options).length > 0 && <Btn small onClick={regenerate}>{zhEn("產生", "Generate")} {combos(options).length} {zhEn("個款式", "variants")}</Btn>}
      </div>
      {optErr && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, margin: "-6px 2px 10px" }}>{optErr}</p>}

      {live.length > 0 && <Label>{zhEn("款式（", "Variants (")}{live.length}{zhEn("）", ")")}</Label>}
      {live.map(r => {
        const dup = dupBarcode(r);
        return (
          <Card key={r.id} style={{ padding: 10, marginBottom: 8, opacity: r.status === "draft" ? 0.6 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ flex: 1, fontWeight: 900, color: C.ink, fontSize: 14.5 }}>{variantLabel(r, options) || zhEn("款式", "variant")}</span>
              {!r._new && <Pill tone={Number(r.stockNow) <= 0 ? "red" : "grey"}>{zhEn("存", "Stock")} {Number(r.stockNow) || 0}</Pill>}
              <button type="button" onClick={() => patch(r.id, { status: r.status === "draft" ? "active" : "draft" })} style={{ border: "none", background: "#F3F2F8", borderRadius: 99, padding: "4px 10px", fontSize: 12, fontWeight: 900, color: C.sub, cursor: "pointer", fontFamily: "inherit" }}>{r.status === "draft" ? zhEn("已停用", "Disabled") : zhEn("停用", "Off")}</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <Mini label={zhEn("售價 ", "Price ") + sym} value={r.price} onChange={v => patch(r.id, { price: v })} />
              <Mini label={zhEn("成本", "Cost")} value={r.cost} onChange={v => patch(r.id, { cost: v })} />
              <Mini label="SKU" value={r.sku} onChange={v => patch(r.id, { sku: v })} text />
              <div style={{ position: "relative" }}>
                <Mini label={zhEn("條碼", "Barcode")} value={r.barcode} onChange={v => patch(r.id, { barcode: v })} text />
                <button type="button" aria-label={zhEn("掃描", "Scan")} onClick={() => onScanOpen((code) => { patch(r.id, { barcode: code }); return "close"; }, zhEn("掃描「", "Scan \"") + variantLabel(r, options) + zhEn("」條碼", "\" barcode"), false)}
                  style={{ position: "absolute", right: 4, bottom: 4, width: 32, height: 30, border: "none", borderRadius: 8, background: C.accentSoft, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ScanLine size={16} /></button>
              </div>
              {r._new && <Mini label={zhEn("開倉數量", "Opening quantity")} value={r.stock} onChange={v => patch(r.id, { stock: v })} />}
            </div>
            {dup && <p style={{ margin: "6px 2px 0", fontSize: 12, fontWeight: 800, color: C.red }}>{zhEn("⚠️ 條碼已經用喺「", "⚠️ Barcode already used by \"")}{dup.name}{zhEn("」", "\"")}</p>}
          </Card>
        );
      })}
      {rows.some(r => r._dropped) && <p style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("移除咗嘅款式會停用（保留銷售紀錄）。", "Removed variants are switched off (sales records are kept).")}</p>}
    </div>
  );
}
const Mini = ({ label, value, onChange, text }) => (
  <label style={{ display: "block" }}>
    <span style={{ fontSize: 11.5, fontWeight: 900, color: C.sub }}>{label}</span>
    <input inputMode={text ? "text" : "decimal"} value={value ?? ""} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, padding: "8px 10px", marginTop: 3 }} />
  </label>
);

// Register: pick which 款式 to add.
export function VariantPicker({ open, parent, variants, sym, heldOther, onPick, onClose }) {
  const options = parent?.web?.options || [];
  const list = useMemo(() => (variants || []).filter(v => v.status !== "draft" && v.sellable), [variants]);
  if (!open || !parent) return null;
  return (
    <Sheet open onClose={onClose} title={parent.name}>
      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <ProductThumb p={parent} size={64} />
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.sub, flex: 1 }}>{[parent.brand, parent.category].filter(Boolean).join(" · ")}<br />{zhEn("揀款式加入購物車", "Choose a variant to add to the cart")}</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 8 }}>
        {list.map(v => {
          const avail = v.trackStock ? v.stock - (heldOther?.(v.id) || 0) : null;
          const price = effectivePrice(v);
          return (
            <button key={v.id} type="button" className="pos-tap" onClick={() => onPick(v)} style={{ border: "1.5px solid " + C.line, background: "#fff", borderRadius: 16, padding: 12, textAlign: "left", fontFamily: "inherit", cursor: "pointer" }}>
              <p style={{ margin: 0, fontWeight: 900, color: C.ink, fontSize: 15 }}>{variantLabel(v, options)}</p>
              <p style={{ margin: "4px 0 0", fontWeight: 900, color: price < v.price ? C.red : C.accent, fontSize: 15 }}>{money(price, sym)}</p>
              {avail != null && <Pill tone={avail <= 0 ? "red" : "grey"} style={{ marginTop: 4 }}>{avail <= 0 ? zhEn("冇貨", "Out of stock") : zhEn("存 ", "Stock ") + avail}</Pill>}
            </button>
          );
        })}
      </div>
      {!list.length && <p style={{ color: C.sub, fontWeight: 800 }}>{zhEn("未有可賣嘅款式", "No sellable variants")}</p>}
    </Sheet>
  );
}

